import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import PluscircleIcon from 'icons/Pluscircle.icon';
import TrashIcon from 'icons/Trash.icon';
import { Button, Select } from 'ncoded-component-library';
import { FieldArray } from 'react-final-form-arrays';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import SelectField from 'components/SelectField';
import useColleagueRoles from 'hooks/useColleagueRoles';
import { AddUsersModal } from 'components/AddUsersModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Colleague, DEAL_ROOM_COLLEAGUE_ROLE } from 'models/DealRoom';
import { COLLEAGUES_INITIAL_VALUE } from '../../../../data';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { getUserFullName, normalizeEmail } from 'utils';

import './DealOwners.styles.scss';
import './DealOwners.styles.responsive.scss';

const ARRAY_HEADER = ['colleagues', 'dataRoom', 'messages', 'notifications'];

type DealOwnersProps = {
  className?: string;
};

const DealOwners: FC<DealOwnersProps> = ({ className }) => {
  const classes = classNames(
    'deal-owners',
    'white-panel',
    'fields-array',
    className,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [ownersOptions, setOwnersOptions] = useState<OptionValue[]>([]);
  const [addColleagueModal, addColleagueModalRef] =
    useCallbackRef<ModalRef>(null);
  const { popServerError } = useContext(PopNotificationsContext);
  const { hasEnterprise, enterprise } = useContext(CurrentUserContext);
  const { t } = useTranslation();
  const {
    values: { colleagues },
  } = useFormState();

  const colleagueRolesOptions = useColleagueRoles();

  const addColleagues = useCallback(
    async (
      emails: string[],
      formValue: Partial<Colleague>[],
      addToForm: (value: Partial<Colleague>) => void,
    ) => {
      try {
        const { data } = await api.users.getSearchByEmails(emails);

        data.forEach(({ email, lastName, firstName }) => {
          const canAddEmail = !formValue.some((el) => el.email === email);

          if (canAddEmail) {
            const name =
              firstName && lastName ? getUserFullName(firstName, lastName) : '';

            addToForm({
              ...COLLEAGUES_INITIAL_VALUE,
              role: DEAL_ROOM_COLLEAGUE_ROLE.CO_BROKER,
              name,
              email,
            });
          }
        });
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError],
  );

  const onSelectAdd = useCallback(
    (option: OptionValue, addToForm: (value: Partial<Colleague>) => void) => {
      addToForm({
        ...COLLEAGUES_INITIAL_VALUE,
        role: DEAL_ROOM_COLLEAGUE_ROLE.CO_BROKER,
        name: option.label.toString(),
        email: option.value,
      });

      setOwnersOptions((old) =>
        old.map((el) =>
          el.value === option.value ? { ...el, disabled: true } : el,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    if (hasEnterprise && !ownersOptions?.length) {
      (async () => {
        try {
          setIsLoading(true);

          const { data } = await api.enterprises.getEnterpriseMembers(
            enterprise?._id,
          );

          setOwnersOptions(
            data.map(({ email, firstName, lastName }) => ({
              value: email,
              label: getUserFullName(firstName, lastName),
              disabled: colleagues.some((el: Colleague) => el.email === email),
            })),
          );
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [
    colleagues,
    hasEnterprise,
    ownersOptions?.length,
    enterprise,
    popServerError,
  ]);

  return (
    <div className={classes}>
      <div>
        {Object.values(ARRAY_HEADER).map((el) => (
          <span key={el}>{t(el)}</span>
        ))}
      </div>
      <FieldArray name="colleagues">
        {({ fields }) => (
          <>
            {fields.map((name, index) => {
              const field = fields?.value[index];

              const isOwner = !!field.isOwner;
              const hasName = !!field.name;

              const ownerLabel = isOwner ? ` (${t('owner')})` : '';

              return (
                <div key={name} className="deal-owners__fields">
                  <div>
                    <div className="deal-owners__colleague">
                      {(hasName || isOwner) && (
                        <span>{`${
                          hasName ? field.name : ''
                        }${ownerLabel}`}</span>
                      )}
                      {field.email && (
                        <span>{normalizeEmail(field.email)}</span>
                      )}
                    </div>
                    <Field
                      name={`${name}.role`}
                      renderAsPortal
                      portalClassName="deal-select-portal"
                      options={colleagueRolesOptions}
                      component={SelectField}
                    />
                  </div>

                  <Field
                    name={`${name}.dataRoomAdmin`}
                    type="checkbox"
                    disabled={isOwner}
                    component={CheckboxField}
                  />
                  <Field
                    name={`${name}.useMessaging`}
                    type="checkbox"
                    component={CheckboxField}
                  />
                  <Field
                    name={`${name}.sendNotifications`}
                    type="checkbox"
                    component={CheckboxField}
                  />

                  {!isOwner ? (
                    <Button
                      variant="icon"
                      icon={TrashIcon}
                      onClick={() => {
                        fields.remove(index);

                        if (hasEnterprise)
                          setOwnersOptions((old) =>
                            old.map(({ disabled, ...rest }) =>
                              rest.value === field.email
                                ? rest
                                : { disabled, ...rest },
                            ),
                          );
                      }}
                    />
                  ) : (
                    <span />
                  )}
                </div>
              );
            })}

            {hasEnterprise ? (
              <Select
                renderAsPortal
                disabled={isLoading}
                portalClassName="deal-owners__select"
                options={ownersOptions}
                onChange={(val: OptionValue) => onSelectAdd(val, fields.push)}
              />
            ) : (
              <>
                <Button
                  variant="link"
                  iconPosition="right"
                  icon={PluscircleIcon}
                  onClick={() => addColleagueModal.open()}
                >
                  {t('addAnotherColleague')}
                </Button>

                <AddUsersModal
                  ref={addColleagueModalRef}
                  title={t('addColleagueToThisDeal')}
                  submitText={t('inviteColleagues')}
                  handleOnSubmit={(values) =>
                    addColleagues(values, fields.value, fields.push)
                  }
                />
              </>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default DealOwners;
